import {
  Layout,
  Menu,
  Button,
  Dropdown,
  Avatar,
  Breadcrumb,
  Space,
  Tooltip,
  notification,
} from 'antd'
import dayjs from 'dayjs'
import { lazy, Suspense } from 'react'

// Lazy so the cmdk + hotkeys code doesn't ship in the initial app shell.
const CommandPalette = lazy(() => import('./CommandPalette'))
import {
  MobileOutlined,
  LogoutOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LockOutlined,
  UserOutlined,
  CalendarOutlined,
  HomeOutlined,
  BellOutlined,
  CloudSyncOutlined,
  DesktopOutlined,
  MutedOutlined,
} from '@ant-design/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { logout } from '../app/authSlice'
import { prefetchRoute } from '../app/lazyPage'
import { APP_CHAT_PATH, buildMenuTree, pageBadgeCounts, withSectionBadges } from '../app/menuTree'
import { canOpenPath, filterMenuTree } from '../app/permissions'
import { useAppDispatch, useAppSelector } from '../app/store'
import { setLanguage, setSidebarCollapsed, toggleSidebar } from '../app/uiSlice'
import { BRAND_LOGO } from '../brand'
import { preventSilentSignIn } from '../features/auth/rememberLogin'
import { useAccessSync } from '../hooks/useAccessSync'
import { useAlertSoundReady } from '../hooks/useAlertSoundReady'
import { useDesktopPush } from '../hooks/useDesktopPush'
import { useLiveRefresh } from '../hooks/useLiveRefresh'
import { useNewVersionAvailable } from '../hooks/useNewVersion'
import { useSignalR } from '../hooks/useSignalR'
import i18n from '../i18n'
import {
  chimeForNotification,
  initAlertSound,
  playAlertChime,
  unlockAlertSound,
} from '../services/alertSound'
import {
  appChatApi,
  bookingConfirmationsApi,
  inboxApi,
  mediaLeadsApi,
  saleContractsApi,
} from '../services/endpoints'
import { notificationsApi, type NotificationDto } from '../services/notifications'
import { stopConnection } from '../services/signalr'
import {
  enableDesktopPush,
  registerServiceWorker,
  releaseDesktopPush,
  syncDesktopPush,
} from '../services/webPush'
import { fmtMoney } from '../utils/format'

import ChangePasswordModal from './ChangePasswordModal'
import HttpErrorPage from './error-pages/HttpErrorPage'
import NotificationCenter from './notifications/NotificationCenter'
import StudioHeader from './StudioHeader'
import AppearanceMenu from './ui/AppearanceMenu'

const { Header, Sider, Content } = Layout

type MenuEntry = { key?: unknown; children?: MenuEntry[]; onMouseEnter?: () => void }

/** مفتاح عنصر القائمة إن كان مساراً (الفواصل والمجموعات بلا مفتاح نصي) */
const pathOf = (e: MenuEntry) => (typeof e.key === 'string' && e.key.startsWith('/') ? e.key : null)

/** عنصر قائمةٍ بمسار: تمرير الفأرة عليه يحمّل ملف صفحته مسبقاً */
function withPrefetch<T extends MenuEntry>(entries: T[]): T[] {
  return entries.map((e) => {
    if (e.children) return { ...e, children: withPrefetch(e.children) }
    const path = pathOf(e)
    return path ? { ...e, onMouseEnter: () => prefetchRoute(path) } : e
  })
}

/** مفتاح القسم الذي يضم المسار الحالي (أطول مسارٍ مطابق) */
function sectionOf(entries: MenuEntry[], path: string): string | null {
  let best: { key: string; len: number } | null = null
  for (const s of entries) {
    if (typeof s.key !== 'string') continue
    for (const c of s.children ?? []) {
      const p = pathOf(c)
      if (p && path.startsWith(p) && (!best || p.length > best.len))
        best = { key: s.key, len: p.length }
    }
  }
  return best?.key ?? null
}

export default function AppLayout() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const nav = useNavigate()
  const loc = useLocation()
  const { user } = useAppSelector((s) => s.auth)
  const { language, collapsed, themeStyle } = useAppSelector((s) => s.ui)
  // صلاحيات الجلسة تتبع ما يحدّده المدير فوراً (القائمة وحارس الصفحات)
  useAccessSync()

  const isSalesMgr = (user?.roles ?? []).some((r) => r === 'Admin' || r === 'SalesManager')
  // «تغيير كلمة المرور» من قائمة الحساب (الثيمان)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [resvCount, setResvCount] = useState(0)
  const [soldCount, setSoldCount] = useState(0)
  useEffect(() => {
    if (isSalesMgr)
      saleContractsApi
        .summary()
        .then((s) => {
          setResvCount(s.pendingPreliminaryReservations ?? 0)
          setSoldCount(s.pendingAppSales ?? 0)
        })
        .catch(() => {})
  }, [isSalesMgr])

  // المستحقات (الحسابات، الإدارة، المدير العام، المبيعات): شارةٌ بالقائمة وتنبيهٌ عند فتح النظام مرةً يومياً
  const [dueCount, setDueCount] = useState(0)
  useEffect(() => {
    if (!user) return
    saleContractsApi
      .dueAlert()
      .then((a) => {
        if (!a.show) return
        setDueCount(a.overdue.count + a.dueToday.count)
        const KEY = `ufuq_due_alert_${dayjs().format('YYYY-MM-DD')}`
        try {
          if (localStorage.getItem(KEY)) return
          localStorage.setItem(KEY, '1')
        } catch {
          /* تخزين محظور — يظهر التنبيه بهذه الجلسة */
        }
        const lines = [
          a.overdue.count > 0
            ? `متأخرة: ${a.overdue.count} — ${fmtMoney(a.overdue.amount)} د.ع`
            : null,
          a.dueToday.count > 0
            ? `تستحق اليوم: ${a.dueToday.count} — ${fmtMoney(a.dueToday.amount)} د.ع`
            : null,
          a.next7.count > 0
            ? `خلال ٧ أيام: ${a.next7.count} — ${fmtMoney(a.next7.amount)} د.ع`
            : null,
        ].filter(Boolean)
        notification.open({
          key: 'due-alert',
          title: a.overdue.count > 0 ? 'مستحقات متأخرة تحتاج متابعة' : 'مستحقات هذا الأسبوع',
          description: (
            <div style={{ lineHeight: 1.8 }}>
              {lines.map((l) => (
                <div key={l}>{l}</div>
              ))}
            </div>
          ),
          placement: 'bottomLeft',
          duration: 0,
          icon: (
            <CalendarOutlined
              style={{ color: a.overdue.count > 0 ? 'var(--aufq-danger)' : 'var(--aufq-warn)' }}
            />
          ),
          actions: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                notification.destroy('due-alert')
                nav('/sales/due-installments')
              }}
            >
              عرض المستحقات
            </Button>
          ),
        })
      })
      .catch(() => {})
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  // إشعارٌ وصل الآن (عميل ميديا جديد، مهمة اتصال، حجز مؤكد…): تنبيه فوري بنغمة —
  // النقر يفتح شاشته ويعلّمه مقروءاً. الجرس والقائمة يتحدثان من الحدث نفسه.
  const userRef = useRef(user)
  useEffect(() => {
    userRef.current = user
  }, [user])
  // صوت التنبيهات: يُفتح مع أول تفاعلٍ للموظف فيرنّ لاحقاً ولو كان التبويب بالخلفية
  useEffect(() => {
    initAlertSound()
  }, [])
  const soundReady = useAlertSoundReady()
  useSignalR<NotificationDto>('NotificationCreated', (n) => {
    if (!userRef.current || !n) return
    // مرةً واحدة لكل إشعار — وإن سأل عنه عامل الخدمة أو رنّ له تبويبٌ آخر
    void chimeForNotification(n.id)
    const key = `notif-${n.id}`
    const tone =
      n.type === 'success'
        ? 'var(--aufq-success)'
        : n.type === 'warning'
          ? 'var(--aufq-warn)'
          : n.type === 'danger'
            ? 'var(--aufq-danger)'
            : 'var(--aufq-accent)'
    notification.open({
      key,
      title: n.title,
      description: n.body,
      placement: 'bottomLeft',
      duration: 8,
      icon: <BellOutlined style={{ color: tone }} />,
      style: { cursor: n.link ? 'pointer' : 'default' },
      onClick: () => {
        notification.destroy(key)
        notificationsApi.markRead(n.id).catch(() => {})
        if (n.link) nav(n.link)
      },
    })
  })

  // نسخةٌ أحدث نُشرت والصفحة مفتوحة: تنبيهٌ بزر «تحديث الآن» — وأول تنقّلٍ لصفحةٍ أخرى
  // يحمّلها من النسخة الجديدة تلقائياً، فلا يبقى تبويبٌ يعمل بنسخةٍ قديمة طوال اليوم
  const newVersion = useNewVersionAvailable()
  const pathAtNewVersion = useRef<string | null>(null)
  useEffect(() => {
    if (!newVersion) return
    if (pathAtNewVersion.current === null) {
      pathAtNewVersion.current = loc.pathname
      notification.open({
        key: 'app-update',
        title: 'نسخة أحدث من النظام جاهزة',
        description: 'فيها آخر التحسينات والإصلاحات. التحديث ثانيةٌ واحدة ولا يُفقد شيءٌ محفوظ.',
        placement: 'bottomLeft',
        duration: 0,
        icon: <CloudSyncOutlined style={{ color: 'var(--aufq-accent)' }} />,
        actions: (
          <Button type="primary" size="small" onClick={() => window.location.reload()}>
            تحديث الآن
          </Button>
        ),
      })
    } else if (loc.pathname !== pathAtNewVersion.current) {
      window.location.reload()
    }
  }, [newVersion, loc.pathname])

  // تنبيهات سطح المكتب (Web Push): عامل الخدمة يُسجَّل مع الدخول، والاشتراك يُزامَن بصمت
  // إن كان الإذن ممنوحاً؛ وإلا يُقترح تفعيلها مرة (الإذن يُطلب بنقرة الموظف حصراً)
  const push = useDesktopPush()
  useEffect(() => {
    if (!user) return
    void registerServiceWorker().then(() => syncDesktopPush())
  }, [user])
  useEffect(() => {
    if (!user || push.permission !== 'default') return
    const KEY = 'ufuq_push_nudged_at'
    try {
      if (Date.now() - Number(localStorage.getItem(KEY) ?? 0) < 3 * 24 * 60 * 60_000) return
      localStorage.setItem(KEY, String(Date.now()))
    } catch {
      /* تخزين محظور — نقترحها الآن ولا نكرر بهذه الجلسة */
    }
    notification.open({
      key: 'push-optin',
      title: 'فعّل تنبيهات سطح المكتب',
      description: 'تصلك الأسماء والمهام الجديدة تنبيهاً على شاشتك فور حدوثها — حتى والنظام مغلق.',
      placement: 'bottomLeft',
      duration: 20,
      icon: <DesktopOutlined style={{ color: 'var(--aufq-accent)' }} />,
      actions: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            // طلب الإذن أول ما يحدث بالنقرة — المتصفحات ترفضه بلا تفاعل
            void enableDesktopPush()
            notification.destroy('push-optin')
          }}
        >
          تفعيل
        </Button>
      ),
    })
  }, [user, push.permission])

  // الإذن ممنوح لكن تجديد اشتراك هذا المتصفح بصمت تعذّر (Safari يشترطه بنقرة) — زرٌّ واحد يعيده
  const pushReenableShown = useRef(false)
  useEffect(() => {
    if (!user || push.permission !== 'granted' || !push.needsAction) return
    if (pushReenableShown.current) return
    pushReenableShown.current = true
    notification.open({
      key: 'push-reenable',
      title: 'أعد تفعيل تنبيهات سطح المكتب',
      description:
        'اشتراك هذا المتصفح بالتنبيهات يحتاج تجديداً بنقرةٍ واحدة — لتصلك الأسماء والمهام على شاشتك فور حدوثها.',
      placement: 'bottomLeft',
      duration: 0,
      icon: <DesktopOutlined style={{ color: 'var(--aufq-warn)' }} />,
      actions: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            // التجديد أول ما يحدث بالنقرة — Safari يرفضه بعد انقضاء أثرها
            void enableDesktopPush()
            notification.destroy('push-reenable')
          }}
        >
          إعادة التفعيل
        </Button>
      ),
    })
  }, [user, push.permission, push.needsAction])

  // النقر على تنبيه النظام: عامل الخدمة يطلب من هذه النافذة فتح شاشة الإشعار بلا إعادة تحميل
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onMessage = (e: MessageEvent) => {
      const m = e.data as { type?: string; url?: string; id?: number | null } | null
      if (!m || m.type !== 'ufuq:open-notification' || !m.url) return
      if (m.id) notificationsApi.markRead(m.id).catch(() => {})
      const u = new URL(m.url, window.location.origin)
      u.searchParams.delete('notif')
      nav(u.pathname + u.search)
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [nav])

  // نافذةٌ فُتحت من تنبيه النظام تحمل ?notif=<id> — يُعلَّم مقروءاً ويُنظَّف العنوان
  useEffect(() => {
    const params = new URLSearchParams(loc.search)
    const id = Number(params.get('notif'))
    if (!id) return
    notificationsApi.markRead(id).catch(() => {})
    params.delete('notif')
    const rest = params.toString()
    nav(loc.pathname + (rest ? `?${rest}` : ''), { replace: true })
  }, [loc.pathname, loc.search, nav])

  // عداد رسائل الواردة غير المقروءة — يظهر شارة على عنصر القائمة
  const hasInbox = (user?.roles ?? []).some(
    (r) => r === 'Admin' || r === 'CallCenterManager' || r === 'CallCenterAgent',
  )
  // شارة «محادثات التطبيق» — لكل الموظفين المؤهلين (المختص يرى عدّه والكول سنتر الكل)
  const [appChatUnread, setAppChatUnread] = useState(0)
  const appChatPrevRef = useRef<number | null>(null)
  // المسار الحالي بمرجع: المؤقت أدناه يُنشأ مرة واحدة ويجب أن يرى أين الموظف الآن
  const pathRef = useRef(loc.pathname)
  useEffect(() => {
    pathRef.current = loc.pathname
  }, [loc.pathname])
  useEffect(() => {
    if (!user) return
    let alive = true
    const onChatPage = () => pathRef.current.startsWith(APP_CHAT_PATH)
    const chatPath = APP_CHAT_PATH
    const applyCount = (n: number, fromServer: boolean) => {
      const prev = appChatPrevRef.current
      appChatPrevRef.current = n
      setAppChatUnread(n)
      // رسالة زبون جديدة وصلت (العدّ زاد) — تنبيه فوري خارج صفحة المحادثات
      if (fromServer && prev !== null && n > prev && !onChatPage()) {
        void playAlertChime()
        notification.open({
          key: 'app-chat-new',
          title: 'رسالة جديدة من زبون التطبيق 💬',
          description: `لديك ${n} رسالة غير مقروءة في محادثات التطبيق`,
          placement: 'bottomLeft',
          duration: 6,
          onClick: () => {
            notification.destroy('app-chat-new')
            nav(chatPath)
          },
          style: { cursor: 'pointer' },
        })
      }
    }
    const tick = () =>
      appChatApi
        .summary()
        .then((s) => alive && applyCount(s.unreadCount ?? 0, true))
        .catch(() => {})
    tick()
    const t = window.setInterval(tick, 20_000)
    const onPush = (e: Event) => applyCount((e as CustomEvent<number>).detail ?? 0, false)
    window.addEventListener('app-chat-unread', onPush)
    return () => {
      alive = false
      window.clearInterval(t)
      window.removeEventListener('app-chat-unread', onPush)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // عنوان التبويب يحمل العدّ حتى يراه الموظف وهو بصفحة أخرى
  /**
   * الهاتف: القائمة الجانبية تلتهم أغلب العرض فتضيق الصفحة — تنطوي تلقائياً
   * دون 992px وتُخفى تماماً دون 768px (يفتحها زر القائمة)، وتعود بالشاشة الواسعة.
   */
  const [isPhone, setIsPhone] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const apply = () => {
      const w = window.innerWidth
      setIsPhone(w < 768)
      dispatch(setSidebarCollapsed(w < 992))
    }
    apply()
    window.addEventListener('resize', apply)
    return () => window.removeEventListener('resize', apply)
  }, [dispatch])

  /**
   * «ستوديو» على الشاشة الواسعة: القائمة جزء من التخطيط بحالتين يبدّلهما زرٌّ على حافتها —
   * موسّعة بعناوينها (الافتراضي) أو شريط أيقونات تنبثق أقسامه عند المرور. لا تطفو فوق الصفحة
   * ولا تنطوي وحدها؛ والاختيار يُحفظ على الجهاز.
   */
  const isStudio = themeStyle === 'studio'
  const [railExpanded, setRailExpanded] = useState(() => {
    try {
      return localStorage.getItem('ufuqerp_studio_pinned') !== '0'
    } catch {
      return true
    }
  })
  const toggleRail = () => {
    const next = !railExpanded
    setRailExpanded(next)
    try {
      localStorage.setItem('ufuqerp_studio_pinned', next ? '1' : '0')
    } catch {
      /* تخزين محجوب — للجلسة فقط */
    }
  }
  const siderCollapsed = isStudio && !isPhone ? !railExpanded : collapsed

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '')
    document.title = appChatUnread > 0 ? `(${appChatUnread}) ${base}` : base
  }, [appChatUnread])

  const [inboxUnread, setInboxUnread] = useState(0)
  useEffect(() => {
    if (!hasInbox) return
    let alive = true
    const tick = () =>
      inboxApi
        .summary()
        .then((s) => alive && setInboxUnread(s.unreadCount ?? 0))
        .catch(() => {})
    tick()
    const t = window.setInterval(tick, 20_000)
    // صفحة الواردة تبثّ العدد عند كل تحديث لها — الشارة تتبعها فوراً
    const onPush = (e: Event) => setInboxUnread((e as CustomEvent<number>).detail ?? 0)
    window.addEventListener('inbox-unread', onPush)
    return () => {
      alive = false
      window.clearInterval(t)
      window.removeEventListener('inbox-unread', onPush)
    }
  }, [hasInbox])

  // شارة «الاتصال بالزبائن»: مهام الاتصال المفتوحة لموظف خدمة العملاء — تُحدَّث بالاستطلاع، وفوراً
  // حين يوزّع المدير أسماءً جديدة (حدث call-tasks الحيّ)، وتتبع صفحة المهام نفسها عند كل تحميل
  const hasCallTasks = (user?.roles ?? []).some((r) => r === 'Admin' || r === 'CallCenterAgent')
  const [callTasksOpen, setCallTasksOpen] = useState(0)
  const callTasksTick = useCallback(() => {
    if (!hasCallTasks) return
    mediaLeadsApi
      .myTasksCount()
      .then((c) => setCallTasksOpen(c.open ?? 0))
      .catch(() => {})
  }, [hasCallTasks])
  useEffect(() => {
    if (!hasCallTasks) return
    callTasksTick()
    const t = window.setInterval(callTasksTick, 20_000)
    const onPush = (e: Event) => setCallTasksOpen((e as CustomEvent<number>).detail ?? 0)
    window.addEventListener('call-tasks-open', onPush)
    return () => {
      window.clearInterval(t)
      window.removeEventListener('call-tasks-open', onPush)
    }
  }, [hasCallTasks, callTasksTick])
  useLiveRefresh(['call-tasks'], callTasksTick)

  // شارة «تأكيد الحجوزات»: حجوزات الدفتر بانتظار اتصال التأكيد — بالاستطلاع، وفوراً بحدثها الحيّ
  const hasConfirmations = (user?.roles ?? []).some((r) =>
    ['Admin', 'CallCenterManager', 'CallCenterAgent', 'Accountant'].includes(r),
  )
  const [pendingConfirmations, setPendingConfirmations] = useState(0)
  const confirmationsTick = useCallback(() => {
    if (!hasConfirmations) return
    bookingConfirmationsApi
      .count()
      .then((c) => setPendingConfirmations(c.pending ?? 0))
      .catch(() => {})
  }, [hasConfirmations])
  useEffect(() => {
    if (!hasConfirmations) return
    confirmationsTick()
    const t = window.setInterval(confirmationsTick, 30_000)
    return () => window.clearInterval(t)
  }, [hasConfirmations, confirmationsTick])
  useLiveRefresh(['booking-confirmations'], confirmationsTick)

  const changeLang = (lng: 'ar' | 'en') => {
    i18n.changeLanguage(lng)
    dispatch(setLanguage(lng))
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = lng
  }

  const handleLogout = async () => {
    // خروجٌ مقصود: لا يعيد مدير كلمات المرور الدخول صامتاً — البريد يبقى جاهزاً فقط
    preventSilentSignIn()
    // اتصال المستخدم الحيّ يُغلق معه — لا يصل من يدخل بعده شيءٌ من إشعاراته
    void stopConnection()
    // واشتراك تنبيهات سطح المكتب يُفكّ قبل ضياع التوكن — دون أن يعطّل الخروج
    await Promise.race([
      releaseDesktopPush({ server: true }),
      new Promise((r) => setTimeout(r, 1500)),
    ])
    dispatch(logout())
    nav('/login')
  }

  // ═══ صلاحيات الصفحات: الأدمن يرى كل شيء، ولوحة التحكم و«بوابتي» للجميع، وغيرهم يرى الأقسام
  // الممنوحة كاملةً والصفحات الممنوحة منفردةً (app/permissions) ═══
  const isAdmin = user?.roles?.includes('Admin') ?? false
  // أدوار المستخدم — تحصر أقساماً فوق المنح (نظام محاسبي للمحاسب والمدير)
  const roles = useMemo(() => user?.roles ?? [], [user?.roles])
  const menuPermissions = user?.menuPermissions
  const permissions = useMemo(() => new Set(menuPermissions ?? []), [menuPermissions])

  const menuBadges = useMemo(
    () => ({
      inbox: inboxUnread,
      appChat: appChatUnread,
      callTasks: callTasksOpen,
      bookingConfirmations: pendingConfirmations,
      sold: soldCount,
      resv: resvCount,
      due: dueCount,
    }),
    [
      inboxUnread,
      appChatUnread,
      callTasksOpen,
      pendingConfirmations,
      soldCount,
      resvCount,
      dueCount,
    ],
  )
  const items = useMemo(
    () => buildMenuTree(t, { isAdmin, badges: menuBadges }),
    [t, isAdmin, menuBadges],
  )

  // ═══ تحميل الصفحات مسبقاً: تمرير الفأرة على عنصرٍ يحمّل ملفه قبل النقر، وبوقت الفراغ تُحمَّل صفحات
  // القسم المفتوح — فالانتقال بين الشاشات لا ينتظر تنزيلاً، ولا يبقى إلا طلب البيانات ═══
  const menuItems = useMemo(
    () =>
      withPrefetch(
        // شارة القسم الأصل (خدمة العملاء، المبيعات…) = مجموع ما يظهر بداخله
        withSectionBadges(
          filterMenuTree(items, permissions, isAdmin, roles),
          pageBadgeCounts(menuBadges),
        ) as unknown as MenuEntry[],
      ) as unknown as any[],
    [items, permissions, isAdmin, roles, menuBadges],
  )
  const openSectionKey = sectionOf(items as unknown as MenuEntry[], loc.pathname)
  // مسارات القسم المفتوح كنصٍّ واحد — يثبت بين التصييرات فلا يُعاد التحميل المسبق إلا عند تغيّر القسم
  const sectionPaths = (
    (items as unknown as MenuEntry[]).find((s) => s.key === openSectionKey)?.children ?? []
  )
    .map(pathOf)
    .filter((k): k is string => k !== null)
    .join('|')
  useEffect(() => {
    const paths = sectionPaths ? sectionPaths.split('|') : []
    if (!paths.length) return
    // requestIdleCallback غير متوفر بكل المتصفحات (سفاري) — مهلة قصيرة بديلاً
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    const idle = (cb: () => void) =>
      w.requestIdleCallback ? w.requestIdleCallback(cb, { timeout: 4000 }) : w.setTimeout(cb, 1500)
    const cancel = (id: number) =>
      w.cancelIdleCallback ? w.cancelIdleCallback(id) : w.clearTimeout(id)
    const id = idle(() =>
      paths.forEach((p, i) => window.setTimeout(() => prefetchRoute(p), i * 150)),
    )
    return () => cancel(id)
  }, [sectionPaths])

  // ═══ حارس المسارات: الصفحة المطلوبة مشتقة من شجرة القائمة نفسها، حتى المتداخلة بالمجموعات الفرعية ═══
  // (دفاع بالعمق فوق تقييد الأدوار في الـ API — يمنع الوصول المباشر بالرابط)
  const accessDenied = !canOpenPath(items, loc.pathname, permissions, isAdmin, roles)

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <a href="#main-content" className="aufq-skip-link">
        تخطّي إلى المحتوى الرئيسي
      </a>
      <Sider
        className="st-sider"
        collapsible
        collapsed={siderCollapsed}
        trigger={null}
        width={isStudio ? 208 : 260}
        collapsedWidth={isPhone ? 0 : isStudio ? 72 : 80}
        style={{
          borderInlineEnd: `1px solid var(--aufq-line)`,
          background: 'var(--aufq-bg-card)',
          // بالهاتف تطفو فوق المحتوى بدل أن تزاحمه
          ...(isPhone && !collapsed
            ? { position: 'fixed', insetInlineStart: 0, top: 0, bottom: 0, zIndex: 1001 }
            : null),
        }}
      >
        {isStudio && !isPhone && (
          <Tooltip title={railExpanded ? 'تصغير القائمة' : 'توسيع القائمة'} placement="left">
            <button
              type="button"
              className="st-edge-toggle"
              aria-expanded={railExpanded}
              aria-label={railExpanded ? 'تصغير القائمة' : 'توسيع القائمة'}
              onClick={toggleRail}
            >
              {railExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            </button>
          </Tooltip>
        )}
        {/* SYSTEM AUFQ brand */}
        <div
          className="st-brand"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: siderCollapsed ? '18px 0' : '18px 20px',
            borderBottom: '1px solid var(--aufq-line-2)',
            flexShrink: 0,
            minHeight: 64,
            justifyContent: siderCollapsed ? 'center' : 'flex-start',
          }}
        >
          <img
            src={BRAND_LOGO}
            alt="UFUQ"
            style={{
              width: 38,
              height: 38,
              borderRadius: 9,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
          {!siderCollapsed && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.1,
                textAlign: language === 'ar' ? 'right' : 'left',
              }}
            >
              <strong
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: '0.02em',
                  color: 'var(--aufq-ink)',
                }}
              >
                {language === 'ar' ? 'أفق' : 'UFUQ'}
              </strong>
              <span
                style={{
                  fontSize: 10.5,
                  color: 'var(--aufq-ink-3)',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--aufq-font-num)',
                  marginTop: 2,
                }}
              >
                {t('app.title')}
              </span>
            </div>
          )}
        </div>
        <div
          className="st-menu-wrap"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: '8px 6px',
          }}
        >
          <Menu
            className="st-menu"
            theme={isStudio ? 'dark' : 'light'}
            subMenuOpenDelay={siderCollapsed ? 0.1 : 0}
            mode="inline"
            selectedKeys={[
              // الإدارة المالية لمجمعٍ بعينه تُبرز مدخلها بالقائمة
              /^\/crm\/residential-complexes\/\d+\/finance$/.test(loc.pathname)
                ? '/crm/residential-complexes/finance'
                : loc.pathname,
            ]}
            style={{ border: 'none', background: 'transparent' }}
            onClick={(e) => nav(e.key)}
            items={menuItems}
          />
        </div>
      </Sider>
      {/* مكان الشريط المطويّ — الشريط نفسه ثابت يطفو فوق المحتوى حين يتّسع */}
      {isPhone && !collapsed && (
        <div
          onClick={() => dispatch(setSidebarCollapsed(true))}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }}
        />
      )}
      <Layout>
        <Header
          className="st-header"
          style={{
            padding: '0 28px',
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 18,
            background: 'var(--aufq-bg-card)',
            borderBottom: `1px solid var(--aufq-line)`,
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          {isStudio ? (
            <StudioHeader
              user={user}
              language={language}
              isPhone={isPhone}
              soundReady={soundReady}
              onMenu={() => dispatch(toggleSidebar())}
              onLang={changeLang}
              onLogout={handleLogout}
              onChangePassword={() => setPasswordOpen(true)}
              onUnlockSound={() => {
                void unlockAlertSound().then((ok) => ok && playAlertChime())
              }}
            />
          ) : (
            <>
              <Space>
                <Button
                  type="text"
                  className="st-icon-btn"
                  aria-label={collapsed ? 'إظهار القائمة' : 'طيّ القائمة'}
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => dispatch(toggleSidebar())}
                  size="large"
                />
                <Breadcrumb
                  style={{ fontSize: 13 }}
                  items={[
                    {
                      title: (
                        <Link to="/">
                          <HomeOutlined />
                        </Link>
                      ),
                    },
                    ...loc.pathname
                      .split('/')
                      .filter(Boolean)
                      .map((seg) => ({
                        title: (
                          <span>{t(`nav.${seg}`, { defaultValue: seg.replace(/-/g, ' ') })}</span>
                        ),
                      })),
                  ]}
                />
              </Space>

              <Space size={6}>
                {user && !soundReady && (
                  <Tooltip title="صوت التنبيهات موقوف حتى تتفاعل مع الصفحة — انقر لتفعيله">
                    <Button
                      type="text"
                      shape="circle"
                      size="large"
                      aria-label="تفعيل صوت التنبيهات"
                      icon={<MutedOutlined style={{ color: 'var(--aufq-warn)' }} />}
                      onClick={() => {
                        // النقرة نفسها تفتح الصوت، ونغمةٌ تجريبية تؤكد أنه يعمل
                        void unlockAlertSound().then((ok) => ok && playAlertChime())
                      }}
                    />
                  </Tooltip>
                )}
                <NotificationCenter />
                <Tooltip title="تطبيق الموظفين — الحضور والطلبات والإشعارات على هاتفك">
                  <Button
                    type="text"
                    shape="circle"
                    size="large"
                    aria-label="تطبيق الموظفين"
                    icon={<MobileOutlined />}
                    onClick={() => window.open('/m/', '_blank', 'noopener')}
                  />
                </Tooltip>
                <AppearanceMenu />
                <Dropdown
                  menu={{
                    items: [
                      { key: 'ar', label: 'العربية', onClick: () => changeLang('ar') },
                      { key: 'en', label: 'English', onClick: () => changeLang('en') },
                    ],
                  }}
                >
                  <Button
                    type="text"
                    shape="circle"
                    className="st-hide-phone"
                    aria-label="اللغة"
                    icon={<GlobalOutlined />}
                    size="large"
                  />
                </Dropdown>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'user',
                        disabled: true,
                        label: (
                          <div style={{ padding: '4px 0' }}>
                            <div style={{ fontWeight: 600 }}>
                              {language === 'ar' ? user?.fullNameAr : user?.fullNameEn}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.7 }}>{user?.email}</div>
                          </div>
                        ),
                      },
                      { type: 'divider' },
                      { key: 'profile', icon: <UserOutlined />, label: t('common.profile') },
                      {
                        key: 'password',
                        icon: <LockOutlined />,
                        label: 'تغيير كلمة المرور',
                        onClick: () => setPasswordOpen(true),
                      },
                      {
                        key: 'logout',
                        icon: <LogoutOutlined />,
                        label: t('app.logout'),
                        onClick: handleLogout,
                      },
                    ],
                  }}
                >
                  <Button
                    type="text"
                    className="st-user-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 40,
                      padding: '0 8px',
                    }}
                  >
                    <Avatar
                      size={32}
                      style={{
                        background: 'var(--aufq-gold-soft)',
                        color: 'var(--aufq-gold)',
                        fontWeight: 700,
                      }}
                    >
                      {(language === 'ar' ? user?.fullNameAr : user?.fullNameEn)?.charAt(0) || (
                        <UserOutlined />
                      )}
                    </Avatar>
                    <span
                      className="st-user-name"
                      style={{ fontSize: 13, fontWeight: 500, color: 'var(--aufq-ink)' }}
                    >
                      {language === 'ar' ? user?.fullNameAr : user?.fullNameEn}
                    </span>
                  </Button>
                </Dropdown>
              </Space>
            </>
          )}
        </Header>
        <Content
          id="main-content"
          tabIndex={-1}
          style={{
            padding: '26px 28px',
            flex: 1,
            minWidth: 0,
            background: 'var(--aufq-bg)',
          }}
        >
          <div key={loc.pathname} className="aufq-fade-in">
            {accessDenied ? <HttpErrorPage status="403" /> : <Outlet />}
          </div>
        </Content>
      </Layout>
      <Suspense fallback={null}>
        <CommandPalette canOpen={(p) => canOpenPath(items, p, permissions, isAdmin, roles)} />
      </Suspense>
      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </Layout>
  )
}
